<template>
	<div class="single-attack-container">
		<button
			type="button"
			class="single-attack"
			:disabled="isDisabled"
			:class="{ 'is-disabled': isDisabled }"
			@click="onClick"
		>
			<div class="single-attack__left">
				<div class="target icon-dark-defeat-32"></div>
				<div class="text">{{ translate("ui.direct_attack") }}</div>
			</div>
			<div class="single-attack__right">
				<div class="damage-value">-{{ attackClickDamage }}</div>
				<AppMainIcon iconName="hearth" :iconSize="16" />
			</div>
		</button>
		<div class="single-attack-container__text">
			{{ translate("ui.direct_attacks") }}:
			<span>{{ attackClickCount }}</span
			>/{{ maxAttackClickCount }}
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import type TileEntity from "@/game/entities/TileEntity.ts";
import { FRIENDLY_ATTACK_CLICK_DAMAGE_MULTIPLIER } from "@/game/SiegeSystemRepository.ts";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter.ts";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository.ts";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";

const { entity } = defineProps<{
	entity: TileEntity;
}>();

const emitter = markRaw(useEmitter());
const ssr = markRaw(useSiegeSystemRepository());

const isDisabled = computed<boolean>(
	(): boolean => !ssr.canPerformAttackClickByTileIndex(entity.index),
);

const attackClickDamage = computed<number | null>((): number | null => {
	return ssr.getFriendlyDamage(
		entity.index,
		FRIENDLY_ATTACK_CLICK_DAMAGE_MULTIPLIER,
	);
});

const attackClickCount = computed<number>(
	(): number => ssr.attackClickCount ?? 0,
);

const maxAttackClickCount = computed<number>(
	(): number => ssr.maxAttackClickCount ?? 0,
);

const onClick = (): void => {
	const damage = ssr.performAttackClickByTileIndex(entity.index);
	emitter.emit(EmitterEvents.SiegeSystemDamage, damage);
};
</script>

<style lang="scss" scoped>
.single-attack-container {
	width: 100%;
	@include flex-col-center;
	gap: 0.75rem;

	&__text {
		color: #f2dcb5;
		font-size: 0.9063rem;

		span {
			color: #f4d278;
		}
	}
}
.single-attack {
	gap: 0.25rem;
	box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
	border-width: 0.075rem;
	border-style: solid;
	border-image-source: linear-gradient(
		to top,
		rgb(189, 144, 50),
		rgb(240, 211, 134)
	);
	border-image-slice: 1;
	background-image: linear-gradient(
		to top,
		rgb(149, 115, 53),
		rgb(225, 202, 161)
	);
	padding: 0.5rem;
	width: 100%;
	@include flex-row-between;
	cursor: pointer;
	color: rgb(55, 38, 21);
	font-weight: 800;
	font-size: 1.0625rem;

	&.is-disabled {
		opacity: 0.3;
		cursor: default;
	}
	&__left {
		@include flex-row-start;
		gap: 0.5rem;
		font-weight: bold;

		.text {
			padding-bottom: 0.25rem;
		}
		.target {
			@include flex-row-center;
			padding-bottom: 0.25rem;
		}
	}
	&__right {
		@include flex-row-start;
		gap: 0.25rem;
		font-weight: bold;
		.damage-value {
			padding-bottom: 0.125rem;
		}
	}
}
</style>
